/**
 * useKeepAwake
 * ------------
 * Requests a screen Wake Lock (Screen Wake Lock API) while the tab is
 * visible so the phone doesn't dim/lock the screen — which is one of the
 * main triggers for the browser to freeze the tab and force a full reload
 * when the user comes back.
 *
 * Behavior:
 *   - Auto-request on mount and whenever the tab becomes visible again.
 *   - Auto-release when the tab goes hidden (the OS will release it anyway).
 *   - Robust against unsupported browsers (iOS <16.4, older Chrome) — no-op.
 *   - Exposes `active` and manual `enable/disable` for opt-in toggles.
 */
import { useEffect, useState, useCallback, useRef } from "react";

const supported = () =>
  typeof navigator !== "undefined" && "wakeLock" in navigator;

export function useKeepAwake({ enabled = true } = {}) {
  const lockRef = useRef(null);
  const [active, setActive] = useState(false);
  const [available] = useState(supported());

  const request = useCallback(async () => {
    if (!supported()) return false;
    try {
      if (lockRef.current && !lockRef.current.released) return true;
      const lock = await navigator.wakeLock.request("screen");
      lockRef.current = lock;
      setActive(true);
      lock.addEventListener("release", () => {
        setActive(false);
      });
      return true;
    } catch (e) {
      // NotAllowedError happens when the tab is not visible / not focused.
      // The visibilitychange handler will retry once the tab is visible.
      setActive(false);
      return false;
    }
  }, []);

  const release = useCallback(async () => {
    try {
      if (lockRef.current) {
        await lockRef.current.release();
      }
    } catch (e) { /* noop */ }
    lockRef.current = null;
    setActive(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      release();
      return;
    }
    request();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        request();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      release();
    };
  }, [enabled, request, release]);

  return { active, available, enable: request, disable: release };
}
